/**
 * DI Overlay Renderer — visual feedback during the Quick-Time DI window.
 *
 * Sakurai principles applied:
 * - Crisp geometric shapes (ring, arrow, arc dots) combined with soft glow
 * - Dark elements mixed into bright effects (dark ring behind glow ring)
 * - Short-lived particles that read at a glance
 * - Characters visible through effects (DI_OVERLAY z-index above tanks but below flash)
 * - Timer bar gives clear time pressure feedback
 *
 * Elements:
 * 1. DI Ring — circle around tank showing perpendicular axis, active segment glows
 * 2. Directional Arrow — shows held input direction with pulsing glow
 * 3. Trajectory Arc — simulated path dots showing predicted trajectory after DI shift
 * 4. Ghost Position — semi-transparent hexagon at predicted landing point
 * 5. Timer Bar — remaining window time
 * 6. Vignette — darkened screen edges to focus attention
 */

import { Container } from 'pixi.js';
import { DI, PHYSICS, WORLD } from '../constants';
import { drawTankShape } from './shapes';
import { lookupTank } from '../tanks';
import type { Player, GameState } from '../types';
import { getDIShiftedVelocity, getDIInputDirection } from '../game/di';
import { getGroundY, isPointInTerrain } from '../terrain/grid';
import type { ParticleSystem } from './particles';
import { getDIHint } from './input-hints';
import { OverlayWindowRenderer } from './overlay-window';

export class DIRenderer extends OverlayWindowRenderer {
    constructor(worldContainer: Container, uiContainer: Container, particles: ParticleSystem) {
        super(worldContainer, uiContainer, particles, DI.PROMPT_FONT_SIZE);
    }

    /**
     * Draw darkened vignette edges (screen-space, drawn once).
     */
    protected drawVignette(): void {
        this.vignetteGfx.clear();
        const w = WORLD.CANVAS_WIDTH;
        const h = WORLD.CANVAS_HEIGHT;

        // Radial gradient approximation — dark edges using concentric rects
        const steps = 6;
        for (let i = 0; i < steps; i++) {
            const t = i / steps;
            const inset = t * Math.min(w, h) * 0.3;
            const alpha = DI.VIGNETTE_ALPHA * (1 - t * t); // quadratic falloff
            this.vignetteGfx.rect(inset, inset, w - inset * 2, h - inset * 2);
            this.vignetteGfx.stroke({ color: 0x000000, width: inset * 0.5, alpha, alignment: 1 });
        }
        // Outer edge solid
        this.vignetteGfx.rect(0, 0, w, h);
        this.vignetteGfx.stroke({ color: 0x000000, width: 40, alpha: DI.VIGNETTE_ALPHA * 0.8, alignment: 1 });
    }

    /**
     * Update DI visuals each frame. Called in variable-timestep loop.
     */
    update(state: GameState, dt: number): void {
        if (!this.active || state.diActivePlayer < 0) {
            if (this.active) this.hide();
            return;
        }

        // In multiplayer, hide DI visuals from opponent — only the DI player sees their own aim
        if (state.multiplayer && state.diActivePlayer !== state.multiplayer.mySlot) {
            this.hide();
            return;
        }

        const player = state.players[state.diActivePlayer];
        const playerColor = player.color;
        const input = getDIInputDirection();
        const hasInput = Math.abs(input.x) > 0.01 || Math.abs(input.y) > 0.01;

        this.drawRing(player, input, hasInput, playerColor, dt);
        this.drawArrow(player, input, hasInput, playerColor, dt);
        this.drawTrajectoryArc(player, playerColor, state.voidY);
        this.drawGhost(player, playerColor, dt);
        this.drawTimerBar(state.diWindowTimer, DI.WINDOW_DURATION, DI.TIMER_BAR_WIDTH, DI.TIMER_BAR_HEIGHT, DI.TIMER_BAR_BG_COLOR);
        this.updatePrompt(player, state.diWindowTimer, hasInput);

        // === Particle effects (Sakurai-audited) ===
        // Knockback angle for ambient perpendicular flickers
        const kbAngle = Math.atan2(player.diPendingVy, player.diPendingVx);

        // Ambient orbiting motes — continuous gentle energy
        this.particles.spawnDIAmbient(player.x, player.y, playerColor, kbAngle);

        // Directional energy when player holds input
        if (hasInput) {
            this.particles.spawnDIInputActive(
                player.x, player.y,
                input.x, input.y,
                playerColor,
            );
        }
    }

    /**
     * Draw the DI ring around the tank. Active segment glows in the input direction.
     */
    private drawRing(
        player: Player, input: { x: number; y: number },
        hasInput: boolean, playerColor: number, dt: number
    ): void {
        this.ringGfx.clear();
        const cx = player.x;
        const cy = player.y;
        const r = DI.RING_RADIUS;
        const segments = DI.RING_SEGMENTS;

        // Perpendicular axis of knockback (for visual guide)
        const kbSpeed = Math.sqrt(player.diPendingVx * player.diPendingVx + player.diPendingVy * player.diPendingVy);
        let perpAngle = 0;
        if (kbSpeed > 0.01) {
            const kbAngle = Math.atan2(player.diPendingVy, player.diPendingVx);
            perpAngle = kbAngle + Math.PI / 2;
        }

        for (let i = 0; i < segments; i++) {
            const a0 = (i / segments) * Math.PI * 2;
            const a1 = ((i + 1) / segments) * Math.PI * 2;
            const midAngle = (a0 + a1) / 2;

            // Check if this segment is near the input direction
            let color: number = DI.RING_INACTIVE_COLOR;
            let alpha = 0.4;
            let width = DI.RING_WIDTH;

            if (hasInput) {
                const inputAngle = Math.atan2(input.y, input.x);
                const angleDiff = Math.abs(Math.atan2(Math.sin(midAngle - inputAngle), Math.cos(midAngle - inputAngle)));
                if (angleDiff < Math.PI / 4) {
                    color = DI.RING_ACTIVE_COLOR;
                    alpha = 0.8 + Math.sin(Date.now() * 0.01) * 0.2;
                    width = DI.RING_WIDTH * 1.5;
                }
            }

            // Highlight perpendicular axis segments (Sakurai: serve as guide, subtle)
            const perpDiff = Math.abs(Math.atan2(Math.sin(midAngle - perpAngle), Math.cos(midAngle - perpAngle)));
            const perpDiff2 = Math.abs(Math.atan2(Math.sin(midAngle - perpAngle - Math.PI), Math.cos(midAngle - perpAngle - Math.PI)));
            if (!hasInput && (perpDiff < Math.PI / 8 || perpDiff2 < Math.PI / 8)) {
                color = playerColor;
                alpha = 0.5 + Math.sin(Date.now() * 0.006) * 0.15;
            }

            const x0 = cx + Math.cos(a0) * r;
            const y0 = cy + Math.sin(a0) * r;
            const x1 = cx + Math.cos(a1) * r;
            const y1 = cy + Math.sin(a1) * r;

            this.ringGfx.moveTo(x0, y0);
            this.ringGfx.lineTo(x1, y1);
            this.ringGfx.stroke({ color, width, alpha });
        }
    }

    /**
     * Draw directional arrow showing held input direction.
     * Sakurai: crisp geometric shape, pulsing glow when active.
     */
    private drawArrow(
        player: Player, input: { x: number; y: number },
        hasInput: boolean, playerColor: number, dt: number
    ): void {
        this.arrowGfx.clear();
        if (!hasInput) {
            return;
        }

        const cx = player.x;
        const cy = player.y;
        const angle = Math.atan2(input.y, input.x);
        const len = DI.ARROW_LENGTH;
        const headSize = DI.ARROW_HEAD_SIZE;

        // Arrow shaft — from ring edge outward
        const startDist = DI.RING_RADIUS + 5;
        const sx = cx + Math.cos(angle) * startDist;
        const sy = cy + Math.sin(angle) * startDist;
        const ex = cx + Math.cos(angle) * (startDist + len);
        const ey = cy + Math.sin(angle) * (startDist + len);

        this.arrowGfx.moveTo(sx, sy);
        this.arrowGfx.lineTo(ex, ey);
        this.arrowGfx.stroke({ color: DI.RING_ACTIVE_COLOR, width: DI.ARROW_WIDTH, alpha: 0.9 });

        // Arrowhead — filled triangle
        const perpX = -Math.sin(angle) * headSize * 0.5;
        const perpY = Math.cos(angle) * headSize * 0.5;
        const tipX = ex + Math.cos(angle) * headSize;
        const tipY = ey + Math.sin(angle) * headSize;

        this.arrowGfx.poly([
            ex + perpX, ey + perpY,
            tipX, tipY,
            ex - perpX, ey - perpY,
        ]);
        this.arrowGfx.fill({ color: DI.RING_ACTIVE_COLOR, alpha: 0.9 });

    }

    /**
     * Draw predicted trajectory arc after DI shift.
     * Simulates physics forward to show where the tank will go.
     * Sakurai: combine crisp dots with soft glow halo.
     */
    private drawTrajectoryArc(player: Player, playerColor: number, voidY: number): void {
        this.arcGfx.clear();

        const shifted = getDIShiftedVelocity(player);
        let px = player.x;
        let py = player.y;
        let vx = player.vx + shifted.vx;
        let vy = player.vy + shifted.vy;

        const gravity = PHYSICS.GRAVITY;
        const dotCount = DI.ARC_DOT_COUNT;
        const stepsPerDot = 3; // physics steps between each dot

        for (let i = 0; i < dotCount; i++) {
            // Simulate forward
            for (let s = 0; s < stepsPerDot; s++) {
                vx *= 0.999; // minimal air friction
                vy += gravity;
                px += vx;
                py += vy;
            }

            // Stop at terrain or void
            if (py > voidY || isPointInTerrain(px, py)) break;
            // Stop at world bounds
            if (px < 0 || px > WORLD.VIRTUAL_WIDTH || py < WORLD.WORLD_TOP) break;

            const t = i / dotCount;
            const alpha = DI.ARC_ALPHA * (1 - t * 0.6); // fade toward end

            // Soft glow behind (Sakurai: combine soft + crisp)
            this.arcGfx.circle(px, py, DI.ARC_GLOW_RADIUS);
            this.arcGfx.fill({ color: playerColor, alpha: DI.ARC_GLOW_ALPHA * (1 - t) });

            // Crisp dot (Sakurai: hard-edged shapes as main focus)
            this.arcGfx.circle(px, py, DI.ARC_DOT_RADIUS);
            this.arcGfx.fill({ color: 0xffffff, alpha });

            // Color ring around dot
            this.arcGfx.circle(px, py, DI.ARC_DOT_RADIUS + 1.5);
            this.arcGfx.stroke({ color: playerColor, width: 1, alpha: alpha * 0.6 });
        }
    }

    /**
     * Draw ghost hexagon at predicted landing position.
     * Pulsing semi-transparent — shows where you'll end up.
     */
    private drawGhost(player: Player, playerColor: number, dt: number): void {
        this.ghostGfx.clear();

        const shifted = getDIShiftedVelocity(player);
        let px = player.x;
        let py = player.y;
        let vx = player.vx + shifted.vx;
        let vy = player.vy + shifted.vy;

        const gravity = PHYSICS.GRAVITY;

        // Simulate until landing (or max steps)
        const maxSteps = 300;
        let landed = false;
        for (let i = 0; i < maxSteps; i++) {
            vx *= 0.999;
            vy += gravity;
            px += vx;
            py += vy;

            // Check ground hit
            const groundY = getGroundY(px);
            if (py + PHYSICS.ECB_RADIUS >= groundY) {
                py = groundY - PHYSICS.ECB_RADIUS;
                landed = true;
                break;
            }

            // World bounds
            if (px < 0 || px > WORLD.VIRTUAL_WIDTH || py < WORLD.WORLD_TOP) break;
        }

        if (!landed) return; // off-screen or in void — no ghost

        // Pulsing alpha
        const pulse = DI.GHOST_ALPHA + Math.sin(Date.now() * 0.001 * DI.GHOST_PULSE_SPEED) * DI.GHOST_PULSE_AMP;

        // Draw per-tank shape at predicted position
        const tankVisual = lookupTank(player.tankId).visual;
        this.ghostGfx.position.set(px, py);
        this.ghostGfx.alpha = pulse;

        // Wireframe ghost at predicted position
        drawTankShape(this.ghostGfx, player.tankId, tankVisual.bodyRadius, playerColor);
        // Bright center dot (readability)
        this.ghostGfx.circle(0, 0, 3);
        this.ghostGfx.fill({ color: 0xffffff });
    }

    /**
     * Update prompt text position and content.
     */
    private updatePrompt(player: Player, timeRemaining: number, hasInput: boolean): void {
        // Position at canvas center-top area
        this.promptText.x = WORLD.CANVAS_WIDTH / 2;
        this.promptText.y = 55;

        // Fade out as time runs low
        const frac = timeRemaining / DI.WINDOW_DURATION;
        this.promptText.alpha = hasInput ? 0.5 : Math.min(1, frac * 2);
        this.promptText.text = hasInput ? 'DI ACTIVE' : getDIHint();
    }
}
